import React, { useState } from 'react';
import { Eye, EyeOff, CheckCircle, XCircle, AlertCircle, Zap, Shield } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password?.length >= 8) score++;
  if (/[A-Z]/?.test(password)) score++;
  if (/[0-9]/?.test(password)) score++;
  if (/[^A-Za-z0-9]/?.test(password)) score++;
  const levels = [
    { score: 0, label: '', color: '' },
    { score: 1, label: 'Weak', color: '#EF4444' },
    { score: 2, label: 'Fair', color: '#F59E0B' },
    { score: 3, label: 'Strong', color: '#10B981' },
    { score: 4, label: 'Very Strong', color: '#00E5FF' },
  ];
  return levels?.[score];
}

export default function Step1Welcome({ partnerProfile, partnerUser, onComplete }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const strength = getPasswordStrength(newPassword);

  const requirements = [
    { label: '8+ characters', met: newPassword?.length >= 8 },
    { label: 'Uppercase letter', met: /[A-Z]/?.test(newPassword) },
    { label: 'Number', met: /[0-9]/?.test(newPassword) },
    { label: 'Special character', met: /[^A-Za-z0-9]/?.test(newPassword) },
  ];

  const allRequirementsMet = requirements?.every(r => r?.met);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!allRequirementsMet) { setError('Password does not meet all requirements.'); return; }
    if (!passwordsMatch) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      // Re-authenticate first
      const { error: signInErr } = await supabase?.auth?.signInWithPassword({
        email: partnerUser?.email,
        password: currentPassword,
      });
      if (signInErr) { setError('Current password is incorrect.'); return; }

      const { error: updateErr } = await supabase?.auth?.updateUser({ password: newPassword });
      if (updateErr) { setError(updateErr?.message); return; }

      setSuccess(true);
      setTimeout(() => onComplete?.(), 1200);
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(0,229,255,0.15)',
    fontFamily: 'DM Sans, sans-serif',
  };

  const tierColors = { basic: '#10B981', silver: '#94A3B8', gold: '#F59E0B', platinum: '#7B2FBE' };
  const tierColor = tierColors?.[partnerProfile?.partner_tier] ?? '#00E5FF';

  return (
    <div className="flex-1 overflow-y-auto px-8 py-8">
      {/* Welcome hero */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(123,47,190,0.15))', border: '1px solid rgba(0,229,255,0.25)' }}
          >
            <Zap size={22} style={{ color: '#00E5FF' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Welcome to FinVisAR, {partnerProfile?.business_name}! 🎉
            </h1>
            <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
              Your partner account has been approved. Let's get your portal set up in 4 quick steps.
            </p>
          </div>
        </div>
      </div>
      {/* Application summary card */}
      <div
        className="rounded-2xl p-5 mb-8"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,229,255,0.1)' }}
      >
        <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Account Summary</p>
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Partner Tier</p>
            <span
              className="inline-block text-xs font-bold px-3 py-1 rounded-full mt-1"
              style={{ background: `${tierColor}20`, color: tierColor, border: `1px solid ${tierColor}40`, fontFamily: 'DM Sans, sans-serif' }}
            >
              {partnerProfile?.partner_tier?.toUpperCase() ?? 'BASIC'}
            </span>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Email</p>
            <p className="text-sm font-medium" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{partnerUser?.email}</p>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Member Since</p>
            <p className="text-sm font-medium" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
              {partnerProfile?.created_at ? new Date(partnerProfile.created_at)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Today'}
            </p>
          </div>
        </div>
      </div>
      {/* Password change form */}
      <div
        className="rounded-2xl p-6"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,229,255,0.1)' }}
      >
        <div className="flex items-center gap-2 mb-5">
          <Shield size={18} style={{ color: '#00E5FF' }} />
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Set Your Permanent Password</h2>
        </div>
        <p className="text-sm mb-5" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
          You received a temporary password via email. Please set a new secure password to continue.
        </p>

        {success ? (
          <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)' }}>
            <CheckCircle size={20} style={{ color: '#00E5FF' }} />
            <p className="text-sm font-semibold" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>Password updated successfully! Proceeding to next step...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current password */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Current (Temporary) Password</label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e?.target?.value)}
                  placeholder="Enter your temporary password"
                  required
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                  style={inputStyle}
                />
                <button type="button" onClick={() => setShowCurrent(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }}>
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* New password */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>New Password</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e?.target?.value)}
                  placeholder="Create a strong password"
                  required
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                  style={inputStyle}
                />
                <button type="button" onClick={() => setShowNew(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }}>
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {/* Strength bar */}
              {newPassword && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4]?.map(i => (
                      <div
                        key={i}
                        className="flex-1 h-1 rounded-full transition-all duration-300"
                        style={{ background: i <= strength?.score ? strength?.color : 'rgba(255,255,255,0.08)' }}
                      />
                    ))}
                  </div>
                  <p className="text-xs" style={{ color: strength?.color, fontFamily: 'DM Sans, sans-serif' }}>{strength?.label}</p>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e?.target?.value)}
                  placeholder="Repeat your new password"
                  required
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                  style={inputStyle}
                />
                <button type="button" onClick={() => setShowConfirm(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }}>
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {confirmPassword && (
                <p className="text-xs mt-1" style={{ color: passwordsMatch ? '#10B981' : '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>
                  {passwordsMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                </p>
              )}
            </div>

            {/* Requirements checklist */}
            <div className="grid grid-cols-2 gap-1.5">
              {requirements?.map((req, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  {req?.met ? (
                    <CheckCircle size={13} style={{ color: '#10B981' }} />
                  ) : (
                    <XCircle size={13} style={{ color: '#475569' }} />
                  )}
                  <span className="text-xs" style={{ color: req?.met ? '#10B981' : '#475569', fontFamily: 'DM Sans, sans-serif' }}>{req?.label}</span>
                </div>
              ))}
            </div>

            {error && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
                <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !allRequirementsMet || !passwordsMatch}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 mt-2"
              style={{
                background: loading || !allRequirementsMet || !passwordsMatch
                  ? 'rgba(0,229,255,0.2)'
                  : 'linear-gradient(135deg, #00E5FF, #7B2FBE)',
                fontFamily: 'DM Sans, sans-serif',
                opacity: loading || !allRequirementsMet || !passwordsMatch ? 0.6 : 1,
                cursor: loading || !allRequirementsMet || !passwordsMatch ? 'not-allowed' : 'pointer',
              }}
            >
              {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {loading ? 'Updating Password...' : 'Update Password & Continue'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
